import { Calendar, CircleAlert, MessageCircle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  addCalendarDays,
  type GetPublicBookingContextResponse,
  type PostPublicBookBody,
  REGIONS_BY_ID,
  toWallClockDate,
} from '@snootsvet/shared/portal'

import { AppointmentChangeWindowClosedAlert } from '@/customer/components/booking/appointment-change-window-closed-alert'
import { BookingContextCard } from '@/customer/components/booking/booking-context-card'
import { BookingNotes } from '@/customer/components/booking/booking-notes'
import { getMondayOfWeek, WeekSlotPicker } from '@/customer/components/booking/week-slot-picker'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { PublicPage } from '@/customer/components/public-page'
import { Alert, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { usePublicAvailableSlots } from '@/customer/hooks/use-public-available-slots'
import { usePublicBookAppointment } from '@/customer/hooks/use-public-book-appointment'
import { usePublicBookingContext } from '@/customer/hooks/use-public-booking-context'
import { captureEvent } from '@/customer/lib/posthog'

/**
 * Public booking page. Handles both fresh bookings and reschedules -
 * the backend decides which mode we're in based on whether the token
 * is already stored on an appointment, and the UI branches on
 * `context.appointmentId`.
 *
 * Reached from staff-issued email links:
 *
 *   /booking?token=<signed>
 *
 * Auth is the booking token alone (no member session), so all data
 * hooks route through `usePublicBookingApi`. The layout / primitives
 * mirror the signed-in portal book + reschedule pages so the visual
 * language stays consistent across surfaces.
 */
export function PublicBookingPage() {
  useDocumentTitle('Your appointment')
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || null

  const contextQuery = usePublicBookingContext(token)

  if (!token) {
    return <PublicMessage message="This link is invalid or has expired" />
  }

  if (contextQuery.isError) {
    return <PublicMessage message={contextQuery.error.message} />
  }

  if (!contextQuery.data) {
    return (
      <PublicPage>
        <LoadingSpinner />
      </PublicPage>
    )
  }

  return <PublicBookingPageInner token={token} context={contextQuery.data} />
}

/**
 * Inner half of the booking page. Split so the outer component can
 * short-circuit on the loading / error paths before we mount the
 * slot query + mutation state.
 */
function PublicBookingPageInner({
  token,
  context,
}: {
  token: string
  context: GetPublicBookingContextResponse
}) {
  const isReschedule = context.appointmentId != null
  const isPastRescheduleDeadline =
    isReschedule &&
    !!context.currentAppointment &&
    new Date() >= new Date(context.currentAppointment.rescheduleDeadline)

  const existingStart = context.currentAppointment
    ? new Date(context.currentAppointment.startTime)
    : null
  const existingNotes = context.currentAppointment?.userNotes ?? ''

  // Anchored on the clinic's current date, not the browser's, so a
  // member browsing from another timezone still lands on the week the
  // clinic is in.
  const minWeekStart = useMemo(
    () => getMondayOfWeek(toWallClockDate(new Date(), context.clinic.timezone)),
    [context.clinic.timezone]
  )
  const [weekStart, setWeekStart] = useState(minWeekStart)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [notes, setNotes] = useState(existingNotes)
  const [confirmed, setConfirmed] = useState(false)

  // Skip the slots query once the member has confirmed (we're on the
  // success screen) or when the reschedule window has closed (nothing
  // to book).
  const slotsQuery = usePublicAvailableSlots(
    token,
    { from: weekStart, to: addCalendarDays(weekStart, 7) },
    { enabled: !confirmed && !isPastRescheduleDeadline }
  )

  const bookMutation = usePublicBookAppointment(token)

  const handleConfirm = () => {
    if (!selectedSlot) return
    const trimmed = notes.trim()
    const body: PostPublicBookBody = {
      startTime: selectedSlot,
      // Mirror the portal reschedule semantics: `undefined` leaves the
      // stored note alone, `null` clears a previously-set note, string
      // sets it. Fresh bookings just send undefined / the trimmed
      // string.
      userNotes: trimmed ? trimmed : isReschedule && existingNotes ? null : undefined,
    }
    bookMutation.mutate(body, {
      onSuccess: ({ appointmentId }) => {
        captureEvent(isReschedule ? 'appointment_rescheduled' : 'appointment_booked', {
          source: 'booking_link',
          appointment_id: appointmentId,
          appointment_type_id: context.appointmentType.id,
          clinic_id: context.clinic.id,
        })
        setConfirmed(true)
      },
    })
  }

  const bookError = bookMutation.error?.message ?? null

  if (confirmed) {
    return (
      <PublicPage
        headline={
          isReschedule
            ? [
                { text: 'All set,', color: 'yellow' },
                { text: `${context.member.firstName}!`, color: 'green' },
              ]
            : [
                { text: `You're booked,`, color: 'yellow' },
                { text: `${context.member.firstName}!`, color: 'blue' },
              ]
        }
      >
        <p className="text-body-lg text-muted-foreground">
          {isReschedule
            ? "We've updated your appointment. A confirmation email is on its way with your new details."
            : "We've sent a confirmation email with the details and links to cancel or reschedule if you need to."}
        </p>
      </PublicPage>
    )
  }

  if (isPastRescheduleDeadline) {
    return (
      <PublicPage
        headline={[
          { text: 'Rescheduling', color: 'yellow' },
          { text: 'no longer', color: 'destructive' },
          { text: 'available', color: 'blue' },
        ]}
      >
        <AppointmentChangeWindowClosedAlert clinicId={context.clinic.id} />
      </PublicPage>
    )
  }

  const timezone = context.clinic.timezone
  // Non-authed surface: source the locale/hour convention from the
  // clinic being booked, since there is no member yet.
  const region = REGIONS_BY_ID[context.clinic.region]

  return (
    <PublicPage
      headline={
        isReschedule
          ? [
              { text: 'Reschedule', color: 'green' },
              { text: `${context.patient.name}'s`, color: 'yellow' },
              { text: 'appointment', color: 'blue' },
            ]
          : [
              { text: 'Pick a time', color: 'yellow' },
              { text: `for ${context.patient.name}`, color: 'blue' },
            ]
      }
    >
      <div className="flex flex-col gap-8">
        <BookingContextCard
          appointmentTypeId={context.appointmentType.id}
          durationMinutes={context.appointmentType.duration}
          clinicId={context.clinic.id}
          start={existingStart ?? undefined}
          region={region}
          timezone={timezone}
          vetName={context.vetName ?? undefined}
        />

        <WeekSlotPicker
          slots={slotsQuery.data?.slots}
          selectedSlot={selectedSlot}
          onSelectSlot={setSelectedSlot}
          weekStart={weekStart}
          onWeekChange={setWeekStart}
          minWeekStart={minWeekStart}
          timezone={timezone}
          region={region}
          loading={slotsQuery.isFetching}
        />

        <BookingNotes id="booking-notes" value={notes} onChange={setNotes} />

        {slotsQuery.isError ? (
          <p className="text-body-sm text-destructive">
            We couldn't load available times. Please try again in a moment.
          </p>
        ) : null}

        {bookError ? (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>{bookError}</AlertTitle>
          </Alert>
        ) : null}

        <Button
          type="button"
          className="w-full"
          disabled={!selectedSlot || bookMutation.isPending}
          onClick={handleConfirm}
        >
          <Calendar data-icon="inline-start" />
          {bookMutation.isPending
            ? isReschedule
              ? 'Rescheduling…'
              : 'Booking…'
            : isReschedule
              ? 'Confirm new time'
              : 'Confirm booking'}
        </Button>
      </div>
    </PublicPage>
  )
}

/**
 * Standalone error / status screen for the public booking flow.
 * Used when the token is missing / expired or the context fetch
 * fails before we have anything to render.
 */
function PublicMessage({ message }: { message: string }) {
  return (
    <PublicPage
      headline={[
        { text: 'This link', color: 'pink' },
        { text: "doesn't work", color: 'yellow' },
      ]}
    >
      <Alert variant="warning">
        <MessageCircle />
        <AlertTitle>{message}</AlertTitle>
      </Alert>
    </PublicPage>
  )
}
